"use client"

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react"
import { ApiError, apiRequestWithAuth } from "@/lib/api-config"

interface VerifyTokenData {
  role: "user" | "admin"
  username: string
  expiresAt: string
}

interface AuthContextType {
  token: string | null
  userType: "user" | "admin" | null
  isAuthenticated: boolean
  isLoading: boolean
  sessionError: string | null
  login: (token: string, type: "user" | "admin") => void
  logout: () => void
  refreshSession: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function clearStoredAuth() {
  localStorage.removeItem("token")
  localStorage.removeItem("userType")
  localStorage.removeItem("adminToken")
  clearRememberedCredentials()
  document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT"
}

function clearRememberedCredentials() {
  for (const key of ["savedUserForm", "savedAdminForm", "savedProUserForm"]) {
    localStorage.removeItem(key)
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null)
  const [userType, setUserType] = useState<"user" | "admin" | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const generation = useRef(0)
  const verification = useRef<AbortController | null>(null)

  const logout = useCallback(() => {
    generation.current++
    verification.current?.abort()
    setToken(null)
    setUserType(null)
    setSessionError(null)
    setIsLoading(false)
    clearStoredAuth()
  }, [])

  const login = useCallback((newToken: string, type: "user" | "admin") => {
    generation.current++
    verification.current?.abort()
    setToken(newToken)
    setUserType(type)
    setSessionError(null)
    setIsLoading(false)
    localStorage.setItem("token", newToken)
    localStorage.setItem("userType", type)
  }, [])

  const refreshSession = useCallback(async (): Promise<boolean> => {
    const attempt = ++generation.current
    verification.current?.abort()
    setToken(null)
    setUserType(null)
    setSessionError(null)
    setIsLoading(true)
    const savedToken = localStorage.getItem("token") || localStorage.getItem("adminToken")
    if (!savedToken) {
      logout()
      return false
    }
    const controller = new AbortController()
    verification.current = controller
    const timeout = setTimeout(() => controller.abort(), 15000)
    try {
      const result = await apiRequestWithAuth<VerifyTokenData>("/verifyToken", savedToken, {
        method: "GET",
        signal: controller.signal,
      }, false) // This attempt owns its failure; a late 401 must not log out a newer session.
      if (attempt !== generation.current || savedToken !== (localStorage.getItem("token") || localStorage.getItem("adminToken"))) return false
      if (result.code === 200 && (result.data?.role === "user" || result.data?.role === "admin")) {
        const role = result.data.role
        setToken(savedToken)
        setUserType(role)
        localStorage.setItem("token", savedToken)
        localStorage.setItem("userType", role)
        return true
      }
      if (result.code === 401 || result.code === 403) logout()
      else setSessionError("暂时无法验证登录状态，请重试。")
      return false
    } catch (error) {
      if (attempt !== generation.current) return false
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) logout()
      else setSessionError("暂时无法验证登录状态，请重试。")
      return false
    } finally {
      clearTimeout(timeout)
      if (attempt === generation.current) {
        verification.current = null
        setIsLoading(false)
      }
    }
  }, [logout])

  useEffect(() => {
    clearRememberedCredentials()
    // 仅开发环境允许 dev_token 注入（截图/本地测试）
    if (process.env.NODE_ENV === "development") {
      try {
        const qs = new URLSearchParams(window.location.search)
        const dt = qs.get("dev_token")
        const dy = qs.get("dev_type")
        if (dt && (dy === "admin" || dy === "user")) {
          login(dt, dy)
          setIsLoading(false)
          return
        }
      } catch {
        /* ignore */
      }
    }

    void refreshSession()
    return () => {
      generation.current++
      verification.current?.abort()
    }
  }, [login, refreshSession])

  return (
    <AuthContext.Provider
      value={{
        token,
        userType,
        isAuthenticated: !!token,
        isLoading,
        sessionError,
        login,
        logout,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
